import type { IncomingHttpHeaders } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import { getAllTestParticipations } from '@packages/abtesting';
import type { Locale } from '@packages/config';
import { TrackingContext } from '@packages/tracking';
import { useUserAgentDevice } from '@packages/shared';
import type { Device } from '@packages/themes/src/default';
import { useCookies } from '@packages/utilities';
import config from '@packages/config';

import type { SearchApiSharedRequestOptions } from '../api/rest/types';
import { getChannelFromDevice } from '../hooks/useChannel';
import { PERSONALIZATION_COOKIE_NAME } from '../hooks/usePersonalizationCookie';
import {
  decodeOrder,
  decodePage,
  decodePromotionId,
  decodeSearchHistoryMarker,
  decodeSelectedFilterValues,
  decodeSuggestMarker,
} from '../queryEncoding';

import { getNitroIdFromCookies } from './getNitroIdFromCookies';

const buildRestParams = (
  query: ParsedUrlQuery,
  locale: Locale,
  device: Device,
  isBot: boolean,
  cookies: Record<string, string>,
  referrer?: string,
  pageNumber?: number,
): SearchApiSharedRequestOptions => {
  const {
    clientId,
    host: { domain },
  } = config;

  const { pageSize, currentPage } = decodePage(query);

  return {
    locale,
    clientId,
    highlight: typeof query.highlight === 'string' ? query.highlight : undefined,
    order: decodeOrder(query),
    filters: decodeSelectedFilterValues(query),
    count: pageSize,
    pageNoDisplay: pageNumber || currentPage || 1,
    ssuggest: decodeSuggestMarker(query),
    searchHistory: decodeSearchHistoryMarker(query),
    isBot,
    personalization: cookies[PERSONALIZATION_COOKIE_NAME],
    nitroId: getNitroIdFromCookies(cookies),
    sessionId: cookies.SESSIONID,
    lmPromo: decodePromotionId(query),
    channel: getChannelFromDevice(device),
    source: referrer?.includes(domain) ? undefined : 'extern',
    testParticipations: Object.fromEntries(
      getAllTestParticipations(cookies).map(({ testId, participantId, variant }) => [
        testId,
        { participantId, variant },
      ]),
    ),
  };
};

export const getServerSideRestParams = (
  query: ParsedUrlQuery,
  locale: Locale,
  headers: IncomingHttpHeaders,
  cookies: Record<string, string>,
  referrer?: string,
) => {
  const device = headers['x-ua-device'] as Device;
  const isBot = headers['x-ua-bot'] !== undefined;

  return buildRestParams(query, locale as Locale, device, isBot, cookies, referrer);
};

export const useClientSideRestParams = (referrer?: string, pageNumber?: number) => {
  const { query } = useRouter();
  const { locale } = useIntl();

  const { isBot } = useContext(TrackingContext) ?? { isBot: false };
  const device = useUserAgentDevice();
  const { getCookies } = useCookies();
  const cookies = getCookies();

  return buildRestParams(query, locale as Locale, device, isBot, cookies, referrer, pageNumber);
};
